import setAuthToken from './setAuthToken';
import { parseJwt } from 'utils';
import store from 'store';
import { setCurrentTeacher } from 'store/actions/authActions';
import Users from 'Api/Users';
import { LSGO_AUTHENTICATION_BASE_URL, LSGO_AUTHENTICATION_CLIENT_ID } from 'config/lsgoAuthentication';
import {
    refreshLsgoAuthenticationToken,
    scheduleLsgoAuthenticationRefresh,
} from './lsgoAuthenticationTokenRefresh';

/**
 * Copied from containers/Auth/LsgoAuthenticationCheck.js's own silent-refresh
 * logic (that page's demo/diagnostic purpose is unchanged; this is the same
 * mechanism made available to the real app boot, not a shared package yet —
 * lsgo-common integration is a deliberate later step, not done here).
 *
 * Base URL and client id are per environment (config/lsgoAuthentication.js).
 */
export { LSGO_AUTHENTICATION_BASE_URL, LSGO_AUTHENTICATION_CLIENT_ID };
const REFRESH_LEAD_SECONDS = 15;

/**
 * Silently restores an lsgo-authentication-issued session on app boot (page
 * refresh / new tab) using the httpOnly refresh-token cookie the browser
 * already holds — no redirect, no user interaction, and no visible loading
 * state beyond what App.js's existing authLoop already shows.
 *
 * Deliberately restore-only: on failure this returns false and does
 * nothing else (no redirect to /authorize) — sidecars alongside the legacy
 * /api/v4/auth/user check (App.js's fetchApiToken), meant only to catch
 * the case where that check finds no legacy session but a real
 * lsgo-authentication one exists; it must never force a user with neither
 * into a new login flow they didn't have before this existed.
 *
 * @param {{ onScheduleRefresh?: (cancel: () => void) => void }} [options] -
 *   called with a cancel function once auto-refresh is scheduled (mirrors
 *   the token's own short TTL), so the caller can clear it on unmount the
 *   same way App.js already clears its legacy authenticationLoop interval.
 * @returns {Promise<boolean>} true if a session was actually restored.
 */
export async function trySilentLsgoAuthenticationLogin({ onScheduleRefresh } = {}) {
    let data;
    try {
        data = await refreshLsgoAuthenticationToken({
            authBaseUrl: LSGO_AUTHENTICATION_BASE_URL,
            clientId: LSGO_AUTHENTICATION_CLIENT_ID,
        });
    } catch {
        return false;
    }

    // claims.sub is lsgo-authentication's own id (a fresh UUID) — not the
    // legacy lsgo user id Users.getUserProfile expects. legacy_user_id is
    // the bridged claim lsgo-authentication mints for exactly this reason
    // (see lsgo's own middleware/security/v6/lsgoAuthenticationApiBridge.js).
    const claims = await parseJwt(data.access_token.replace('Bearer ', ''));
    if (!claims?.legacy_user_id) return false;

    setAuthToken(data.access_token);
    try {
        const profile = await Users.getUserProfile(claims.legacy_user_id);
        if (!profile.data.name) profile.data.name = profile.data.firstName;
        store.dispatch(setCurrentTeacher({ ...profile.data }));
    } catch {
        // Token was valid but the legacy profile lookup failed — don't
        // leave a half-authenticated axios header behind.
        setAuthToken(false);
        return false;
    }

    if (onScheduleRefresh) {
        const cancel = scheduleLsgoAuthenticationRefresh({
            expiresInSeconds: data.expires_in,
            leadSeconds: REFRESH_LEAD_SECONDS,
            onDue: () => trySilentLsgoAuthenticationLogin({ onScheduleRefresh }),
        });
        onScheduleRefresh(cancel);
    }

    return true;
}
